// Everything that is specific to TAM's business lives here. The engine (spend
// sync, FX, cohorts, attribution, reporting) reads this file and nothing else
// for business facts. Both the Worker and the dashboard import it.

export const BUSINESS_NAME = "TAM Dental";
export const PRODUCT_NAME = "TAM Ads";
export const REPORTING_TIMEZONE = "Asia/Riyadh";
export const PRODUCTION_ORIGIN = "https://ads.tam.dental";
// Kept from the TAM Local Ads era on purpose: connected AI clients hold tokens
// for this server name and scope, and renaming them would disconnect everyone.
export const MCP_SERVER_NAME = "tam-local-ads";
export const MCP_SCOPE = "tam-local.read";

// Reporting (base) currency. Every stored "base_minor" amount is in halalas.
export const BASE_CURRENCY = "SAR" as const;
// Currencies an advertising account may bill in besides the base currency.
// Adding one here is the only code change needed; the FX sync picks it up.
export const QUOTE_CURRENCIES = ["USD"] as const;
export const CURRENCIES = [BASE_CURRENCY, ...QUOTE_CURRENCIES] as const;
export type Currency = (typeof CURRENCIES)[number];

// Fixed pegs, expressed as quote-currency units per 1 base unit. SAR is pegged
// to USD by SAMA at 3.75 SAR per USD. Frankfurter (ECB reference rates) has no
// SAR, so unpegged quote currencies are derived through USD: quote per USD
// (Frankfurter) × USD per SAR (this peg).
export const BASE_PER_USD = 3.75; // exact in binary floating point (15/4)
export const USD_PER_BASE = 1 / BASE_PER_USD;
export const FIXED_RATES: Partial<Record<Currency, { nativePerBase: number; source: string }>> = {
  USD: { nativePerBase: USD_PER_BASE, source: "PEG_SAMA_3.75" },
};

// Projects. One dashboard, one database, two businesses: every project-owned
// row carries `project`, and every read and write names one. Money is always
// stored in the base currency (SAR minor units); a project only chooses the
// currency it is DISPLAYED and planned in. A display currency must be the base
// currency or a fixed peg, so the conversion never depends on a monthly rate.
// The migrations' CHECK (project IN (...)) mirror PROJECT_IDS.
export const PROJECT_IDS = ["local", "international"] as const;
export type ProjectId = (typeof PROJECT_IDS)[number];
export const DEFAULT_PROJECT: ProjectId = "local";

export type ProjectRule = {
  id: ProjectId;
  // Shown on every page ("TAM Local · 2026 advertising report").
  label: string;
  // The header switch, and its phone-width form.
  shortLabel: string;
  compactLabel: string;
  description: string;
  displayCurrency: "SAR" | "USD";
  // GoHighLevel sub-account of this project (Phase B puller, routes/ghl.ts):
  // the Worker secret holding its private-integration token and the var
  // holding its location ID.
  ghlTokenSecret: "GHL_LOCAL_TOKEN" | "GHL_INTERNATIONAL_TOKEN";
  ghlLocationVar: "GHL_LOCAL_LOCATION_ID" | "GHL_INTERNATIONAL_LOCATION_ID";
};

export const PROJECTS: readonly ProjectRule[] = [
  { id: "local", label: "TAM Local", shortLabel: "Local", compactLabel: "Local", description: "TAM Dental's clinic business in Jeddah", displayCurrency: "SAR",
    ghlTokenSecret: "GHL_LOCAL_TOKEN", ghlLocationVar: "GHL_LOCAL_LOCATION_ID" },
  { id: "international", label: "TAM International", shortLabel: "International", compactLabel: "Intl", description: "TAM Dental's dental-tourism business", displayCurrency: "USD",
    ghlTokenSecret: "GHL_INTERNATIONAL_TOKEN", ghlLocationVar: "GHL_INTERNATIONAL_LOCATION_ID" },
] as const;

export const isProjectId = (value: unknown): value is ProjectId => typeof value === "string" && PROJECT_IDS.some((project) => project === value);
export const projectRule = (project: ProjectId): ProjectRule => {
  const rule = PROJECTS.find((candidate) => candidate.id === project);
  if (!rule) throw new Error(`Missing project rule: ${project}`);
  return rule;
};
export const displayCurrency = (project: ProjectId): Currency => projectRule(project).displayCurrency;

// Base units per one unit of a display currency: 1 for SAR, 3.75 for USD.
export const basePerDisplayUnit = (currency: Currency): number => {
  if (currency === BASE_CURRENCY) return 1;
  if (currency === "USD") return BASE_PER_USD;
  throw new Error(`${currency} is not pegged to ${BASE_CURRENCY}; it cannot be a display currency.`);
};
// Base minor units (halalas) → display minor units (cents for USD). Unrounded,
// so totals are converted once rather than summed from rounded parts.
export const baseToDisplayMinor = (baseMinor: number, currency: Currency): number => baseMinor / basePerDisplayUnit(currency);
// Base minor units → display major units (SAR or USD), for API amounts.
export const baseToDisplayMajor = (baseMinor: number, currency: Currency): number => baseToDisplayMinor(baseMinor, currency) / 100;
// Display minor units → base minor units, or null when the amount has no exact
// base equivalent (USD cents that are not a multiple of 4 give fractional halalas).
export const displayToBaseMinor = (displayMinor: number, currency: Currency): number | null => {
  const base = displayMinor * basePerDisplayUnit(currency);
  return Number.isSafeInteger(base) ? base : null;
};

// First month the system reports on; earlier months are never synced. TAM
// Local's ROAS report covers 2025 and 2026 (Abdul, 20 Oct 2026); the
// spend history is filled by the backfill queue (worker/lib/spend-backfill.ts).
export const EARLIEST_MONTH = "2025-01";

export const ROLES = ["ADMINISTRATOR", "VIEWER"] as const;
export type Role = (typeof ROLES)[number];

export const PLATFORM_IDS = ["meta", "google", "snapchat", "tiktok"] as const;
export type PlatformId = (typeof PLATFORM_IDS)[number];
export const PLATFORM_GROUPS = ["META", "GOOGLE", "SNAPCHAT", "TIKTOK"] as const;
export type PlatformGroup = (typeof PLATFORM_GROUPS)[number];

export type PlatformRule = {
  id: PlatformId;
  group: PlatformGroup;
  label: string;
  source: "SYNC" | "MANUAL";
  // Composio toolkit slug holding the platform connection.
  composioToolkit: string;
  // False while the platform's sync is still a TODO (see routes/composio.ts).
  syncImplemented: boolean;
  // Billing currency assumed for a platform before an account says otherwise.
  defaultCurrency: Currency;
  // CRM lead-source text (lower-cased, trimmed) that attributes a payment or
  // lead to this platform. First match wins, in PLATFORM_RULES order, so an
  // explicit platform word (Meta first) beats the TikTok lead-gen heuristics
  // below: "Dr.Nader Insta Form" is Meta even though a doctor's TikTok forms
  // look similar. Rules decided by Abdul on 20 Oct 2026 from the live Local
  // pull (docs/decisions.md "Local attribution rules"); strings that must
  // stay unattributed on purpose: "Website - Google (organic)", "Jotform
  // Dr.X", "Dr. Abdulelah", "Dr.Salma", "Dr.Nader-A15", "Bleaching &
  // Cleaning 2025 Ramadan Cam.", "Organic", "ENDO", "Business Chat".
  leadSourcePatterns: readonly RegExp[];
};

export const PLATFORM_RULES: readonly PlatformRule[] = [
  { id: "meta", group: "META", label: "Meta", source: "SYNC", composioToolkit: "metaads", syncImplemented: true, defaultCurrency: "USD",
    leadSourcePatterns: [
      /^(facebook|instagram|meta|fb)\b/, /\b(facebook|instagram|meta|fb) ads?\b/, /\b(meta|facebook|instagram|fb)\b.*\bads?\b/,
      // "ig", "instagram", "Dr.Nader Insta Form", "استمارة حساب د.نادر انستقرام 2", "English Formfb", "English Formig".
      /\b(ig|instagram|insta)\b/, /انستقرام/, /\bfb\b|facebook|meta ads?/, /form(fb|ig)$/,
    ] },
  { id: "google", group: "GOOGLE", label: "Google", source: "SYNC", composioToolkit: "googleads", syncImplemented: true, defaultCurrency: "USD",
    leadSourcePatterns: [/^(google|search|adwords|youtube)\b/, /\bgoogle ads?\b/, /\b(google|youtube)\b.*\bads?\b/] },
  { id: "snapchat", group: "SNAPCHAT", label: "Snapchat", source: "SYNC", composioToolkit: "snapchat", syncImplemented: true, defaultCurrency: "USD",
    leadSourcePatterns: [/^snap/, /\bsnapchat\b/] },
  { id: "tiktok", group: "TIKTOK", label: "TikTok", source: "SYNC", composioToolkit: "tiktok_ads", syncImplemented: true, defaultCurrency: "USD",
    leadSourcePatterns: [
      /^tik\s?tok/, /\btik\s?tok\b/, /\bttclid\b/,
      // TikTok lead-gen form names as the sub-account records them: a bare
      // 15–20 digit form/ad-group id ("6298553231197401939"), "Ad group
      // 20251209034103", "مجموعة إعلانية 20260215024447", "Lead
      // generation_June_2026", "First Lead Gen. Dr.Salma + Smart", "First New
      // Smart & Manual Lead", "Lead 2 Dr.Salma TikTok+ Smart", "5G", "New Lead
      // _split_test", "Automatic TargetingAd group 20260819054328…".
      /^\d{15,20}$/, /^ad group \d+/, /^مجموعة إعلانية/, /lead generation|lead gen\b/, /smart ?\+|\+ ?smart\b|smart & manual/, /^5g$/, /split_test/, /^new lead\b/, /automatic targeting/,
    ] },
] as const;

// Dated attribution overrides, checked AFTER the platform rules above for a
// source no rule matched. None are active.
//
// History: on 20 Oct 2026 Abdul decided that for TAM Local, until September
// 2026, website and no-source leads count as Google. On 2 Nov 2026 he
// replaced it: "until we started detecting UTMs from Google… merge both
// Google and website organic… under one, which is basically website… What we
// contribute to Google directly is stuff that has UTM parameters and came
// through Google." So Google is only what a Google tag names ("Website -
// Google Ads", a Google UTM); every other website and no-source lead is the
// non-advertising "Website (Google Ads & SEO)" channel
// (worker/lib/source-families.ts CHANNELS). The 20 Oct rule read:
// until September 2026 the website had no tracking, so every website lead
// ("Website", "website", "Website, Contact Us Arabic", "Website - Google
// (organic)", "Website - chatgpt.com"…) and every lead with no source at all
// ("", "(none)", "No lead source") counts as Google (website, ads and SEO).
// From October 2026 only the explicit labels attribute ("Website - Google
// Ads", "Website - Meta Ads", "Website - TikTok Ads", matched by the rules
// above); organic, direct and chatgpt website leads stay unattributed. The
// cohort month (the month the contact was created) decides which side of
// the boundary a lead is on.
export type AttributionOverride = {
  project: ProjectId;
  platformId: PlatformId;
  // Inclusive cohort-month range; null = open at that end.
  fromMonth: string | null;
  throughMonth: string | null;
  patterns: readonly RegExp[];
  // Whether an empty / "(none)" / "No lead source" source matches too.
  matchesEmpty: boolean;
  // The reason shown in the proof drawer and the lead-source report.
  reason: string;
  // Short note under the platform's card on the ROAS report.
  cardNote: string;
};

// 2 Nov 2026 (Abdul, later the same day): "for 2025-2026, let's merge all
// website, Google Ads and SEO with Google Ads… We used to advertise to the
// website, and SEO and ads were together… we don't want things spread in
// different places." So for TAM Local, lead months 2025-01 … 2026-12, every
// website lead and every lead with no source counts as Google, with the
// proof saying which basis applies: the Google Ads UTM tag where it exists
// (the platform rule, "Website - Google Ads"), no measurement for an
// untagged website lead, or the UTM's own reading (SEO, direct, AI, social)
// for a tagged one that was not an ad.
export const ATTRIBUTION_OVERRIDES: readonly AttributionOverride[] = [
  { project: "local", platformId: "google", fromMonth: "2025-01", throughMonth: "2026-12", patterns: [/^website\s*(,.*)?$/], matchesEmpty: true,
    reason: "website lead with no UTM measurement (tracking began mid-September 2026); the website's traffic was Google Ads, SEO and direct visitors, counted together as Google for 2025–2026",
    cardNote: "incl. website: Ads, SEO, direct" },
  { project: "local", platformId: "google", fromMonth: "2025-01", throughMonth: "2026-12", patterns: [/^website\s*[-–—]/], matchesEmpty: false,
    reason: "website lead whose UTM shows a non-ad visit (SEO, direct, AI assistant or social); counted with Google for 2025–2026 by decision",
    cardNote: "incl. website: Ads, SEO, direct" },
] as const;

// Sources no platform rule may claim, whatever words they contain: organic
// traffic is not advertising ("Website - Google (organic)", "Website -
// Facebook/Instagram (organic)"). A dated override may still apply to them
// (Local's website rule until September 2026).
export const UNATTRIBUTABLE_PATTERNS: readonly RegExp[] = [/\(organic\)/, /^organic$/, /^reactivated by ads\b/] as const;

// Sources that mean "no source at all" for the override above.
export const EMPTY_LEAD_SOURCES: readonly string[] = ["", "(none)", "none", "no lead source", "null", "undefined"] as const;

export const platformRule = (platformId: PlatformId): PlatformRule => {
  const rule = PLATFORM_RULES.find((candidate) => candidate.id === platformId);
  if (!rule) throw new Error(`Missing platform rule: ${platformId}`);
  return rule;
};
export const platformForGroup = (group: PlatformGroup): PlatformRule => {
  const rule = PLATFORM_RULES.find((candidate) => candidate.group === group);
  if (!rule) throw new Error(`Missing platform group: ${group}`);
  return rule;
};
export const SYNCED_GROUPS: readonly PlatformGroup[] = PLATFORM_RULES.filter((rule) => rule.source === "SYNC" && rule.syncImplemented).map((rule) => rule.group);

export const isCurrency = (value: string): value is Currency => CURRENCIES.some((currency) => currency === value);
export const isPlatformId = (value: string): value is PlatformId => PLATFORM_IDS.some((platform) => platform === value);
export const isPlatformGroup = (value: string): value is PlatformGroup => PLATFORM_GROUPS.some((group) => group === value);
export const isRole = (value: string): value is Role => ROLES.some((role) => role === value);
